import type { PropsWithChildren } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  FormField,
  FormPatternInput,
  HelperText,
  Icon,
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
  Tooltip,
} from '@/_components';
import { type CountryQuerySchema, sanitizeCountry } from '@/_schemas/countries';
import type { PhoneNumberFormSchema } from '@/_schemas/phone-numbers';
import { getSafeNumberParts } from '@/_schemas/phone-numbers';

import { btnStyle } from './style';
import { phoneTypeIcon, typesOptions } from './type-options';

export function FormFields({
  form,
  countries = [],
  isEditable = true,
  children,
  onValidateDuplicate,
}: PropsWithChildren<{
  form: UseFormReturn<PhoneNumberFormSchema>;
  countries: CountryQuerySchema[];
  defaultCountryValue: PhoneNumberFormSchema['country'];
  isEditable?: boolean;
  id: number;
  onValidateDuplicate: () => void;
}>) {
  const [country, number] = form.watch(['country', 'number']);

  const { allFormatsWithCallingCode, mask } = getSafeNumberParts(number, country.calling_code, country.phone_format);

  let errorMsg = '';
  const errorKeys = Object.keys(form.formState.errors);
  if (errorKeys.length > 0) {
    errorMsg = form.formState.errors[errorKeys[0] as keyof typeof form.formState.errors]?.message ?? '';
  }

  return (
    <article className="w-full">
      <section className="flex flex-nowrap items-start gap-1">
        <FormField
          control={form.control}
          name="type"
          render={({ field: { value, onChange } }) => (
            <Menubar>
              <MenubarMenu>
                <MenubarTrigger>
                  <div className="mt-px flex pt-3">
                    <Tooltip content={value?.name}>
                      <Button
                        tag="div"
                        title={value?.name}
                        as="outline"
                        size="sm"
                        disabled={!isEditable}
                        className={btnStyle}
                      >
                        <Avatar transparent size="sm" className="border-0">
                          <Icon icon={phoneTypeIcon(value?.value)} />
                        </Avatar>
                      </Button>
                    </Tooltip>
                  </div>
                </MenubarTrigger>
                <MenubarContent className="max-h-80 max-w-72 overflow-y-auto">
                  {typesOptions.map((item) => {
                    const { name, value: nValue } = item;

                    return (
                      <MenubarItem
                        key={name}
                        className="flex items-center gap-2"
                        onClick={() => {
                          onChange(item);
                        }}
                      >
                        <Avatar transparent size="sm" className="border-0">
                          <Icon icon={phoneTypeIcon(nValue)} />
                        </Avatar>

                        <span>{name}</span>
                      </MenubarItem>
                    );
                  })}
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          )}
        />

        <FormField
          control={form.control}
          name="country"
          render={({ field: { value, onChange } }) => (
            <Menubar>
              <MenubarMenu>
                <MenubarTrigger>
                  <div className="mt-px flex pt-3">
                    <Tooltip content={`${value?.name}: ${value?.calling_code}`}>
                      <Button
                        tag="div"
                        title={value?.name}
                        as="outline"
                        size="sm"
                        disabled={!isEditable}
                        className={btnStyle}
                      >
                        <Avatar size="sm">
                          <AvatarImage src={value?.flag?.url} />
                          <AvatarFallback name={value?.name ?? 'Unknown'} />
                        </Avatar>
                      </Button>
                    </Tooltip>
                  </div>
                </MenubarTrigger>
                <MenubarContent className="max-h-80 max-w-72 overflow-y-auto">
                  {countries
                    .filter((item) => item.calling_code)
                    .map((item) => {
                      const { name, id, calling_code, flag } = item;
                      return (
                        <MenubarItem
                          key={id}
                          className="flex justify-between gap-2"
                          onClick={() => {
                            onChange(sanitizeCountry(item));
                            form.setValue('number', '', { shouldDirty: true });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Avatar size="sm">
                              <AvatarImage src={flag?.url} />
                              <AvatarFallback className="text-[0.625rem]" name={name} />
                            </Avatar>
                            <span>{name}</span>
                          </div>

                          <span className="ml-auto">{calling_code}</span>
                        </MenubarItem>
                      );
                    })}
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          )}
        />

        <FormField
          control={form.control}
          name="number"
          render={({ field }) => (
            <div className="relative flex-1">
              <FormPatternInput
                {...field}
                hideMsg
                onValueChange={field.onChange}
                format={allFormatsWithCallingCode}
                mask={mask}
                allowEmptyFormatting
                disabled={!isEditable}
                className="pl-1"
                onBlur={onValidateDuplicate}
              />
              {children}
            </div>
          )}
        />
      </section>
      {errorMsg && <HelperText className="text-error text-xs">{errorMsg}</HelperText>}
    </article>
  );
}
